import { useId } from "react"

import { useTranslation } from "react-i18next"

import type { AdminTrainingModuleRow, AdminTrainingSectionRow } from "@/api/admin-api"

import { ChevronDown, ChevronRight, Pencil, Shield, ShieldCheck } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Switch } from "@/components/ui/switch"
import { cn } from "@/lib/utils"

export function TrainingModuleBlock({
  mod,
  expanded,
  onToggleExpanded,
  onToggleModule,
  onToggleSection,
  onEditModule,
  onEditModuleAccess,
  onEditSectionAccess,
  updatingModuleId,
  updatingSectionKey,
}: {
  mod: AdminTrainingModuleRow
  expanded: boolean
  onToggleExpanded: () => void
  onToggleModule: (moduleId: string, enabled: boolean) => void
  onToggleSection: (moduleId: string, sectionId: string, enabled: boolean) => void
  onEditModule?: (moduleId: string) => void
  onEditModuleAccess: () => void
  onEditSectionAccess: (sectionId: string) => void
  updatingModuleId: string | undefined
  updatingSectionKey: string | undefined
}) {
  const { t } = useTranslation()
  const sectionsId = useId()

  return (
    <div className="bg-card rounded-lg border">
      <div className="flex items-center gap-2">
        <button
          aria-controls={sectionsId}
          aria-expanded={expanded}
          aria-label={`Toggle ${mod.display_name} sections`}
          className="text-muted-foreground hover:text-foreground flex items-center justify-center p-2"
          type="button"
          onClick={onToggleExpanded}
        >
          {expanded ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
        </button>
        <div className="flex flex-1 flex-wrap items-center justify-between gap-2 py-3 pr-3">
          <div>
            <div className="font-medium">{mod.display_name}</div>
            <div className="text-muted-foreground text-xs">{mod.id}</div>
          </div>
          <div className="flex items-center gap-2">
            {onEditModule ? (
              <Button
                size="icon"
                type="button"
                variant="ghost"
                aria-label={t("adminDashboard.trainingModuleEdit.edit", {
                  defaultValue: "Edit training module",
                })}
                onClick={() => onEditModule(mod.id)}
              >
                <Pencil className="size-4" />
              </Button>
            ) : null}
            <Button
              className="flex items-center gap-1"
              size="sm"
              type="button"
              variant="outline"
              onClick={onEditModuleAccess}
            >
              {mod.access_configured ? (
                <ShieldCheck className="text-success size-4" />
              ) : (
                <Shield className="size-4" />
              )}
              {t("adminDashboard.access.access", { defaultValue: "Access" })}
            </Button>
            <Switch
              aria-label={`${mod.display_name} status`}
              checked={mod.enabled}
              disabled={updatingModuleId === mod.id}
              onCheckedChange={(v) => {
                onToggleModule(mod.id, v)
              }}
            />
          </div>
        </div>
      </div>
      {expanded && (
        <div className="border-t px-4 pb-3 pl-10" id={sectionsId}>
          <div className="text-muted-foreground hidden grid-cols-12 gap-2 border-b py-2 text-xs font-medium lg:grid">
            <div className="col-span-5">
              {t("adminDashboard.trainingManagement.section", "Section")}
            </div>
            <div className="col-span-4 text-right">
              {t("adminDashboard.access.access", "Access")}
            </div>
            <div className="col-span-3 text-right">
              {t("adminDashboard.features.status", "Status")}
            </div>
          </div>
          {mod.sections.map((sec) => (
            <TrainingSectionRow
              key={sec.id}
              mod={mod}
              sec={sec}
              updatingSectionKey={updatingSectionKey}
              onEditSectionAccess={onEditSectionAccess}
              onToggleSection={onToggleSection}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function TrainingSectionRow({
  mod,
  sec,
  onToggleSection,
  onEditSectionAccess,
  updatingSectionKey,
}: {
  mod: AdminTrainingModuleRow
  sec: AdminTrainingSectionRow
  onToggleSection: (moduleId: string, sectionId: string, enabled: boolean) => void
  onEditSectionAccess: (sectionId: string) => void
  updatingSectionKey: string | undefined
}) {
  const { t } = useTranslation()
  const key = `${mod.id}:${sec.id}`

  return (
    <div
      className={cn(
        "admin-responsive-grid-row grid grid-cols-12 items-center gap-2 border-b py-2 text-sm last:border-0",
        !sec.catalog_enabled && "opacity-60"
      )}
    >
      <div className="col-span-5">
        <div>{sec.display_name}</div>
        <div className="text-muted-foreground text-xs">{sec.id}</div>
      </div>
      <div className="col-span-4 flex justify-end">
        <span className="text-muted-foreground mr-auto text-xs font-medium lg:hidden">Access</span>
        <Button
          className="flex items-center gap-1"
          size="sm"
          type="button"
          variant="ghost"
          onClick={() => {
            onEditSectionAccess(sec.id)
          }}
        >
          {sec.access_configured ? (
            <ShieldCheck className="text-success size-4" />
          ) : (
            <Shield className="size-4" />
          )}
          {t("adminDashboard.access.access", { defaultValue: "Access" })}
        </Button>
      </div>
      <div className="col-span-3 flex justify-end">
        <span className="text-muted-foreground mr-auto text-xs font-medium lg:hidden">Status</span>
        <Switch
          aria-label={`${sec.display_name} status`}
          checked={sec.enabled}
          disabled={updatingSectionKey === key}
          onCheckedChange={(v) => {
            onToggleSection(mod.id, sec.id, v)
          }}
        />
      </div>
    </div>
  )
}
